export type Lang = "nl" | "en" | "de";
export const LANGS: Lang[] = ["nl", "en", "de"];
export const DEFAULT_LANG: Lang = "nl";

export const LANG_LABEL: Record<Lang, string> = {
  nl: "Nederlands",
  en: "English",
  de: "Deutsch",
};

export const SITE_URL = "https://openluchtfilmfestival.nl";

export const pathForLang = (l: Lang) => (l === "nl" ? "/" : `/${l}`);
export const urlForLang = (l: Lang) => `${SITE_URL}${pathForLang(l)}`;

export type Dict = {
  nav: {
    programma: string;
    beleving: string;
    sfeer: string;
    praktisch: string;
    faq: string;
    tickets: string;
    dates: string;
    menuOpen: string;
    menuClose: string;
    partners: string;
    volunteer: string;
    terrain: string;
    gettingHere: string;
    weatherComfort: string;
    accessibility: string;
  };
  common: {
    buyTickets: string;
    viewProgram: string;
    receiveProgram: string;
    backToTop: string;
    moreInfo: string;
    language: string;
    openInMaps: string;
  };
  hero: {
    badge: string;
    title1: string;
    title2: string;
    title3: string;
    intro: string;
    dates: string;
    datesValue: string;
    location: string;
    locationValue: string;
    capacity: string;
    capacityOutside: string;
    capacityCovered: string;
    note: string;
    startLabel: string;
    startValue: string;
    startSub: string;
    tickerNow: string;
    tickerTime: string;
    tickerParking: string;
    heroAlt: string;
  };
  why: {
    eyebrow: string;
    title: string;
    body: string;
    stats: { value: string; suffix: string; caption: string }[];
    reasons: { title: string; body: string }[];
  };
  email: {
    badge: string;
    title1: string;
    title2: string;
    body: string;
    bullets: string[];
    firstName: string;
    firstNamePlaceholder: string;
    email: string;
    emailPlaceholder: string;
    submit: string;
    submitting: string;
    success: string;
    disclaimer: string;
    required: string;
    invalidEmail: string;
  };
  gallery: {
    eyebrow: string;
    title1: string;
    title2: string;
    body: string;
    captions: { screen: string; audience: string; horeca: string; couple: string; drinks: string };
    alts: { screen: string; audience: string; horeca: string; couple: string; drinks: string };
  };
  practical: {
    eyebrow: string;
    title1: string;
    title2: string;
    cards: { title: string; lines: string[] }[];
  };
  travel: {
    eyebrow: string;
    title1: string;
    title2: string;
    body: string;
    mapLabel: string;
    address: string;
    mapsUrl: string;
    modes: { title: string; tag: string; body: string }[];
    freeParking: {
      badge: string;
      title: string;
      body: string;
      note: string;
      walk: string;
      cta: string;
      disclaimer: string;
      mapsUrl: string;
    };
  };
  weather: {
    eyebrow: string;
    title1: string;
    title2: string;
    body: string;
    tips: { title: string; body: string }[];
  };
  accessibility: {
    eyebrow: string;
    title: string;
    body: string;
    wheelchair: { title: string; body: string };
    golfcart: { title: string; body: string };
    question: { title: string; body: string };
  };
  faq: {
    eyebrow: string;
    title1: string;
    title2: string;
    intro: string;
    items: { q: string; a: string }[];
  };
  finalCta: {
    eyebrow: string;
    title1: string;
    title2: string;
    body: string;
    note: string;
  };
  footer: {
    tagline: string;
    edition: string;
    location: string;
    contact: string;
    followUs: string;
    rights: string;
    copyright: string;
    privacy: string;
    terms: string;
    ticketPartner: string;
  };
  sticky: { teaser: string; dates: string };
  seo: {
    home: {
      title: string;
      description: string;
      ogTitle: string;
      ogDescription: string;
    };
  };
};

const nl: Dict = {
  nav: {
    programma: "Programma",
    beleving: "Beleving",
    sfeer: "Sfeer",
    praktisch: "Praktisch",
    faq: "FAQ",
    tickets: "Koop tickets",
    dates: "14 t/m 22 aug 2026",
    menuOpen: "Menu openen",
    menuClose: "Menu sluiten",
    partners: "Partners",
    volunteer: "Vrijwilliger worden?",
    terrain: "Terrein",
    gettingHere: "Bereikbaarheid",
    weatherComfort: "Weer & comfort",
    accessibility: "Toegankelijkheid",
  },
  common: {
    buyTickets: "Koop tickets",
    viewProgram: "Bekijk programma",
    receiveProgram: "Ontvang het programma",
    backToTop: "Terug naar boven",
    moreInfo: "Meer info",
    language: "Taal",
    openInMaps: "Open in Google Maps →",
  },
  hero: {
    badge: "EDITIE 32 · TICKETS NU IN DE VERKOOP",
    title1: "Grote films.",
    title2: "Onder de sterren",
    title3: "van Stadspark Berg & Bos.",
    intro:
      "Negen zomeravonden lang verandert de bosweide van Stadspark Berg & Bos in de openluchtbioscoop van Apeldoorn. Zodra de zon ondergaat en het doek oplicht, begint een filmavond die je nergens anders beleeft.",
    dates: "Speeldata",
    datesValue: "14 t/m 22 aug",
    location: "Locatie",
    locationValue: "Berg & Bos · Apeldoorn",
    capacity: "Capaciteit",
    capacityOutside: "2.000 buiten",
    capacityCovered: "500 overdekt",
    note: "Populaire avonden zijn snel vol",
    startLabel: "Aanvang",
    startValue: "20:00 — 21:15",
    startSub: "Terrein open 20:00 · film start wanneer het donker genoeg is (21:00–21:15)",
    tickerNow: "● Now playing soon — Editie 2026",
    tickerTime: "Terrein open 20:00 · Doek op wanneer het donker genoeg is (21:00–21:15)",
    tickerParking: "Parkeren · 15 min lopen vanaf ingang",
    heroAlt: "Openluchtbioscoop in het bos van Berg & Bos bij avond",
  },
  why: {
    eyebrow: "— Waarom je erbij wilt zijn",
    title: "Een zomeravond die je niet aan een gewone bioscoop verspilt.",
    body: "Grote films, een groot doek, het bos om je heen en de geur van de zomer. Geen popcornlucht, geen rode pluche, wel schemering, sterren en het zachte gezoem van het publiek.",
    stats: [
      { value: "9", suffix: "avonden", caption: "Van 14 t/m 22 augustus" },
      { value: "2.000", suffix: "buitenplaatsen", caption: "Vrij kiezen op de weide" },
      { value: "500", suffix: "overdekt", caption: "Droog op de tribune" },
      { value: "200 m²", suffix: "groot doek", caption: "Midden in het bos" },
    ],
    reasons: [
      {
        title: "Een filmzaal in het bos",
        body: "Geen plafond, geen muren, alleen het bladerdak van Stadspark Berg & Bos en een doek dat oplicht zodra de schemering valt.",
      },
      {
        title: "De zomertraditie van Apeldoorn",
        body: "Al jaren de avond waar Apeldoorners naar uitkijken. Een uitje dat blijft hangen lang nadat de aftiteling voorbij is.",
      },
      {
        title: "Eten, drinken, blijven hangen",
        body: "Horeca op het terrein voor en tijdens de film. Schuif aan met vrienden, deel een fles wijn, laat de avond duren.",
      },
      {
        title: "Zorgeloos heen en terug",
        body: "Parkeren bij de hoofdingang, gratis fietsenstalling en een korte boswandeling naar het terrein.",
      },
    ],
  },
  email: {
    badge: "Early access",
    title1: "Wees er als eerste bij,",
    title2: "vóór de rest van Apeldoorn.",
    body: "Ontvang het programma in je inbox, krijg een privélink naar de voorverkoop en hoor het direct als populaire avonden bijna uitverkocht zijn.",
    bullets: [
      "Programma direct in je mailbox",
      "24 uur vóór publieke verkoopstart",
      "Geen ruis. Alleen festivalupdates.",
    ],
    firstName: "Voornaam",
    firstNamePlaceholder: "Bijv. Sanne",
    email: "E-mailadres",
    emailPlaceholder: "jij@voorbeeld.nl",
    submit: "Houd mij op de hoogte",
    submitting: "Een moment…",
    success: "Je staat op de lijst. We laten je als eerste weten wanneer de kaartverkoop start.",
    disclaimer: "Geen spam. Alleen relevante updates over het festival. Afmelden kan altijd.",
    required: "Vul dit veld in",
    invalidEmail: "Vul een geldig e-mailadres in",
  },
  gallery: {
    eyebrow: "— Sfeer",
    title1: "Het laatste licht boven de bomen.",
    title2: "Het doek licht op.",
    body: "Het zachte gezoem van het publiek, de geur van zomeravond, de eerste klanken die over de weide rollen. Zo voelt een avond op Stadspark Berg & Bos.",
    captions: {
      screen: "Het grote doek · schemering",
      audience: "2.500 zitplaatsen",
      horeca: "Horeca · voor en tijdens de film",
      couple: "Samen onder de sterren",
      drinks: "Een avond om te laten duren",
    },
    alts: {
      screen: "Filmdoek in het bos bij avond",
      audience: "Publiek op de bosweide",
      horeca: "Horeca met sfeerverlichting in het bos",
      couple: "Stel kijkt samen film in de openlucht",
      drinks: "Glas wijn bij lampjes in de avond",
    },
  },
  practical: {
    eyebrow: "— Op het terrein",
    title1: "Alles wat je moet weten",
    title2: "voor je komt.",
    cards: [
      {
        title: "Locatie",
        lines: ["Bosweide Berg & Bos", "J.C. Wilslaan 21, 7313 HK Apeldoorn"],
      },
      {
        title: "Zitplaatsen",
        lines: [
          "2.000 plekken buiten · 500 op overdekte tribune",
          "Vrije plaatskeuze; geen vaste stoel per ticket",
          "Eigen lage stoel meenemen mag, maar hoeft niet",
        ],
      },
      {
        title: "Horeca op locatie",
        lines: ["Hapjes, drankjes en zomerse cocktails", "Open vanaf 20:00 tot ruim na de aftiteling"],
      },
      {
        title: "Toiletten & EHBO",
        lines: ["Gratis toiletten op het terrein", "EHBO aanwezig gedurende elke avond"],
      },
      {
        title: "Bioscoopcadeau",
        lines: [
          "Bewaar je ticket: in september 2e kaartje gratis bij Cinemajestic Zutphen op vertoon van dit ticket.",
          "Geldig t/m 30 september 2026. Per (e-)ticket één keer geldig.",
        ],
      },
      {
        title: "Eigen sfeer mag",
        lines: ["Plaid, kussen of lage stoel? Welkom.", "Eigen alcohol en glas op het terrein niet toegestaan"],
      },
    ],
  },
  travel: {
    eyebrow: "— Bereikbaarheid",
    title1: "Zo kom je er.",
    title2: "Zo kom je weer thuis.",
    body: "Berg & Bos ligt op een prachtige boslocatie net buiten het centrum van Apeldoorn. Een korte wandeling door het bos hoort bij de avond, neem makkelijke schoenen mee.",
    mapLabel: "Routekaart",
    address: "J.C. Wilslaan 21, Apeldoorn",
    mapsUrl: "https://maps.app.goo.gl/TM6ntXiRm8DtR1RP7",
    modes: [
      {
        title: "Met de fiets",
        tag: "Aanrader",
        body: "Gratis fietsenstalling vlak bij de ingang. Vanaf daar is het ongeveer 15 minuten lopen over het bospad naar de weide.",
      },
      {
        title: "Met de auto",
        tag: "Parkeren",
        body: "Parkeer bij de hoofdingang van Berg & Bos. Vanaf de parkeerplaats wandel je in ongeveer 15 minuten door het park naar het terrein.",
      },
      {
        title: "Met het OV",
        tag: "Let op",
        body: "Vanaf station Apeldoorn rijdt een bus richting de J.C. Wilslaan. Daarna nog circa 15 minuten lopen. Na afloop is het OV vaak beperkt, check de retourtijden.",
      },
      {
        title: "Lopen vanaf ingang",
        tag: "± 15 minuten",
        body: "Vanaf de hoofdingang van Berg & Bos loop je in zo'n 15 minuten naar de bosweide. Aangegeven met bordjes en sfeerverlichting bij avondvoorstellingen.",
      },
    ],
    freeParking: {
      badge: "Gratis parkeren",
      title: "Gratis parkeren via de Asselsestraat",
      body: "Kom je met de auto? Aan het einde van de Asselsestraat in Apeldoorn, voorbij Stayokay, kun je gratis parkeren langs de openbare weg. Deze parkeerplek ligt bij het hondenlosloopbos en de ingang van Stadspark Berg en Bos. Volg de aanwijzingen van de verkeersregelaars.",
      note: "Let op: dit is een andere locatie dan de betaald parkeerterreinen van Apenheul en Berg en Bos aan de J.C. Wilslaan.",
      walk: "Vanaf de parkeerplek loop je via het bos in de richting van het festivalterrein. Volg ter plaatse de bewegwijzering en houd rekening met een korte wandeling.",
      cta: "Open route in Google Maps",
      disclaimer:
        "Parkeren is op eigen verantwoordelijkheid. Beschikbaarheid kan variëren; volg altijd lokale verkeersborden en aanwijzingen.",
      mapsUrl: "https://www.google.com/maps/search/?api=1&query=52.206375,5.919837",
    },
  },
  weather: {
    eyebrow: "— Weer & comfort",
    title1: "Een zomeravond",
    title2: "die altijd doorgaat.",
    body: "We kijken buiten, dat hoort erbij. Met de juiste laag kleding en een goede stemming wordt elke avond, zon of regen, een avond om te onthouden.",
    tips: [
      {
        title: "Regen? Geen probleem.",
        body: "Het festival gaat in principe ook door bij regen. Alleen bij onveilig weer wijken we uit.",
      },
      {
        title: "Kleed je warm aan",
        body: "Het koelt in het bos sneller af dan je denkt. Een extra trui of jas is altijd een goed idee.",
      },
      {
        title: "Dichte schoenen",
        body: "Het is een bosweide. Sneakers of stevige schoenen zitten comfortabeler dan slippers.",
      },
      {
        title: "Laat de paraplu thuis",
        body: "Een poncho of regenjas houdt jou droog én het zicht van anderen vrij. Win win.",
      },
    ],
  },
  accessibility: {
    eyebrow: "— Toegankelijkheid",
    title: "Welkom, ongeacht hoe je bij ons komt.",
    body: "We doen ons best om iedereen onbezorgd van een filmavond te laten genieten. Heb je specifieke wensen? Laat het ons weten, dan denken we mee.",
    wheelchair: {
      title: "Rolstoeltoegankelijk",
      body: "Het terrein is toegankelijk met rolstoel. Er is een eigen, vlakke plek vooraan voor rolstoelgebruikers en begeleiders.",
    },
    golfcart: {
      title: "Golfkarservice",
      body: "Voor minder mobiele bezoekers rijdt een golfkarretje tussen de ingang en de bosweide — geen lange wandeling nodig.",
    },
    question: {
      title: "Vraag waar je rekening mee wil houden?",
      body: "Stuur ons gerust een bericht via de contactgegevens onderaan de pagina. We helpen je graag een avond te plannen waar je je op je gemak voelt.",
    },
  },
  faq: {
    eyebrow: "— FAQ",
    title1: "Even snel",
    title2: "checken.",
    intro: "Staat je vraag er niet bij? Stuur ons een bericht via de contactgegevens onderaan.",
    items: [
      {
        q: "Krijg ik ook iets extra's bij mijn ticket?",
        a: "Ja! Je ticket van het Openluchtfilmfestival is ook je toegang tot een bioscoopcadeau. In september kun je ermee naar Cinemajestic in Zutphen: op vertoon van je (e-)ticket krijg je een tweede bioscoopkaartje gratis. Zo geniet je nog een extra keer van film, dit keer binnen. Geldig t/m 30 september 2026, één keer per ticket.",
      },
      {
        q: "Kan ik een vaste stoel reserveren?",
        a: "Nee. Een ticket geeft toegang tot het terrein en de overdekte tribune, maar reserveert geen vaste stoel. Wie vroeg is, kiest de beste plek.",
      },
      {
        q: "Wat gebeurt er als het regent?",
        a: "In principe gaat het festival ook door bij regen. Neem warme kleding en eventueel een poncho mee. Laat paraplu's thuis — die belemmeren het zicht.",
      },
      {
        q: "Kan ik mijn tickets annuleren?",
        a: "Het annuleringsbeleid hangt af van onze ticketpartner. Zodra de kaartverkoop start, vind je de exacte voorwaarden bij elk ticket.",
      },
      {
        q: "Is het terrein toegankelijk met een rolstoel?",
        a: "Ja. De bosweide is glooiend, maar toegankelijk. Voor minder mobiele bezoekers rijdt een golfkarretje tussen de ingang en het terrein.",
      },
      {
        q: "Zijn honden toegestaan?",
        a: "Huisdieren zijn niet toegestaan op het terrein. Begeleidings- en hulphonden zijn uiteraard wél welkom.",
      },
      {
        q: "Hoe laat moet ik aanwezig zijn?",
        a: "Het terrein is open vanaf 20:00 uur — ruim voor aanvang. De films starten wanneer het donker genoeg is, tussen 21:00 en 21:15. Kom op je gemak vroeg, dan kun je nog een hapje eten en een plekje uitzoeken op de bosweide.",
      },
      {
        q: "Kan ik eten en drinken kopen op het terrein?",
        a: "Ja. Op locatie is horeca aanwezig met hapjes en drankjes. Ideaal om al voor de film aan te schuiven.",
      },
      {
        q: "Hoe werkt parkeren?",
        a: "Parkeren bij de hoofdingang van Berg & Bos. Vanaf de parkeerplaats is het ongeveer 15 minuten lopen naar de bosweide.",
      },
      {
        q: "Is er overdekte zitplek?",
        a: "Ja, er zijn 500 overdekte zitplaatsen op de tribune, naast 2.000 zitplaatsen.",
      },
      {
        q: "Kan ik ook kaartjes aan de deur kopen?",
        a: "Ja, mits er plek is. Je kunt tickets aan de deur kopen op de avond zelf, zolang de voorstelling niet is uitverkocht. Online vooraf kopen is wel aan te raden — dan ben je zeker van een plek en vermijd je de rij.",
      },
    ],
  },
  finalCta: {
    eyebrow: "● Editie 2026 · 14 — 22 augustus · Berg & Bos",
    title1: "Zorg dat je erbij bent",
    title2: "deze zomer.",
    body: "Negen avonden, één plek, één zomer. Boek op tijd voor de populairste filmavonden, ze zijn vaak verrassend snel weg.",
    note: "● Online of aan de deur · zolang de voorraad strekt",
  },
  footer: {
    tagline:
      "Negen zomeravonden openluchtbioscoop op de bosweide van Stadspark Berg & Bos. Grote films, buiten in het groen, midden in de zomer.",
    edition: "● Editie 2026 · 14 — 22 augustus",
    location: "Locatie",
    contact: "Contact",
    followUs: "Volg ons",
    rights:
      'Organisatie: <a href="https://deliciousmedia.nl" target="_blank" rel="noopener noreferrer" class="underline hover:text-gold">Delicious Media</a>. Alle rechten voorbehouden',
    copyright: "© {year} Openluchtfilmfestival Apeldoorn",
    privacy: "Privacy",
    terms: "Voorwaarden",
    ticketPartner: "Ticketpartner",
  },
  sticky: { teaser: "Wees er als eerste bij", dates: "● 14 — 22 aug · Berg & Bos" },
  seo: {
    home: {
      title: "Openluchtfilmfestival Apeldoorn · 14 — 22 augustus 2026",
      description:
        "Negen zomeravonden openluchtbioscoop op de bosweide van Stadspark Berg & Bos in Apeldoorn. Grote films onder de sterren, horeca op locatie en zomerse sfeer.",
      ogTitle: "Openluchtfilmfestival Apeldoorn — Editie 2026",
      ogDescription:
        "14 t/m 22 augustus 2026 — openluchtbioscoop op de bosweide van Stadspark Berg & Bos. Mis de start van de kaartverkoop niet.",
    },
  },
};

const en: Dict = {
  nav: {
    programma: "Programme",
    beleving: "Experience",
    sfeer: "Atmosphere",
    praktisch: "Visit info",
    faq: "FAQ",
    tickets: "Get tickets",
    dates: "14 — 22 Aug 2026",
    menuOpen: "Open menu",
    menuClose: "Close menu",
    partners: "Partners",
    volunteer: "Become a volunteer?",
    terrain: "On site",
    gettingHere: "Getting here",
    weatherComfort: "Weather & comfort",
    accessibility: "Accessibility",
  },
  common: {
    buyTickets: "Get tickets",
    viewProgram: "View programme",
    receiveProgram: "Get the programme",
    backToTop: "Back to top",
    moreInfo: "More info",
    language: "Language",
    openInMaps: "Open in Google Maps →",
  },
  hero: {
    badge: "32ND EDITION · TICKETS ON PRE-SALE SOON",
    title1: "Great films.",
    title2: "Under the stars",
    title3: "of Stadspark Berg & Bos.",
    intro:
      "For nine summer evenings the forest meadow of Stadspark Berg & Bos turns into Apeldoorn's open-air cinema. As the sun sets and the screen lights up, an unforgettable movie night begins.",
    dates: "Dates",
    datesValue: "14 — 22 Aug",
    location: "Location",
    locationValue: "Berg & Bos · Apeldoorn",
    capacity: "Capacity",
    capacityOutside: "2,000 outdoor",
    capacityCovered: "500 covered",
    note: "Popular nights sell out fast",
    startLabel: "Start time",
    startValue: "20:00 — 21:15",
    startSub: "Grounds open 20:00 · film starts when dark enough (21:00–21:15)",
    tickerNow: "● Now playing soon — 2026 edition",
    tickerTime: "Grounds open 20:00 · Film starts when dark enough (21:00–21:15)",
    tickerParking: "Parking · 15 min walk from the entrance",
    heroAlt: "Open-air cinema in the forest of Berg & Bos at night",
  },
  why: {
    eyebrow: "— Why you'll want to be there",
    title: "A summer night you won't waste on an ordinary cinema.",
    body: "Great films, a huge screen, the forest around you and the scent of summer. No popcorn smell, no red plush — just dusk, stars and the soft hum of the crowd.",
    stats: [
      { value: "9", suffix: "nights", caption: "From 14 to 22 August" },
      { value: "2,000", suffix: "outdoor seats", caption: "Pick your spot on the meadow" },
      { value: "500", suffix: "covered", caption: "Dry on the grandstand" },
      { value: "200 m²", suffix: "huge screen", caption: "Right in the forest" },
    ],
    reasons: [
      {
        title: "A cinema inside a forest",
        body: "No ceiling, no walls — only the canopy of Stadspark Berg & Bos and a screen that lights up the moment dusk falls.",
      },
      {
        title: "Apeldoorn's summer tradition",
        body: "For years the night locals look forward to. An outing that stays with you long after the credits roll.",
      },
      {
        title: "Eat, drink, linger",
        body: "Food and drinks on site before, during and after the film. Pull up a chair, share a bottle of wine, let the evening last.",
      },
      {
        title: "Easy in, easy out",
        body: "Parking at the main entrance, free bike storage and a short forest walk to the meadow.",
      },
    ],
  },
  email: {
    badge: "Early access",
    title1: "Be the first to know,",
    title2: "ahead of the rest of Apeldoorn.",
    body: "Get the programme straight to your inbox, a private link to the pre-sale and a heads-up the moment popular nights are almost sold out.",
    bullets: [
      "Programme straight to your inbox",
      "24 hours before the public sale opens",
      "No noise. Festival updates only.",
    ],
    firstName: "First name",
    firstNamePlaceholder: "E.g. Alex",
    email: "Email address",
    emailPlaceholder: "you@example.com",
    submit: "Keep me posted",
    submitting: "One moment…",
    success: "You're on the list. We'll let you know the moment ticket sales start.",
    disclaimer: "No spam. Just relevant festival updates. Unsubscribe anytime.",
    required: "Please fill in this field",
    invalidEmail: "Please enter a valid email address",
  },
  gallery: {
    eyebrow: "— Atmosphere",
    title1: "The last light above the trees.",
    title2: "The screen lights up.",
    body: "The soft hum of the crowd, the scent of a summer evening, the first sounds rolling across the meadow. That's a night at Stadspark Berg & Bos.",
    captions: {
      screen: "The big screen · dusk",
      audience: "2,000 seats",
      horeca: "Food & drinks · before and after the film",
      couple: "Together under the stars",
      drinks: "An evening to make last",
    },
    alts: {
      screen: "Film screen in the forest at night",
      audience: "Audience on the forest meadow",
      horeca: "Food stands with ambient lighting in the forest",
      couple: "Couple watching an open-air film together",
      drinks: "Glass of wine by string lights at night",
    },
  },
  practical: {
    eyebrow: "— On site",
    title1: "Everything you need to know",
    title2: "before you come.",
    cards: [
      { title: "Location", lines: ["Forest meadow Stadspark Berg & Bos", "J.C. Wilslaan 21, 7313 HK Apeldoorn"] },
      {
        title: "Seating",
        lines: [
          "1,500 seats outdoors · 500 on the covered grandstand",
          "Free seating — no assigned seats per ticket",
          "Bringing a low chair is allowed but not required",
        ],
      },
      {
        title: "Food & drinks on site",
        lines: ["Snacks, drinks and summer cocktails", "Open from 20:00 until well after the credits"],
      },
      { title: "Toilets & first aid", lines: ["Free toilets on the grounds", "First aid present every evening"] },
      {
        title: "Cinema gift",
        lines: [
          "Keep your ticket: in September get a 2nd cinema ticket free at Cinemajestic Zutphen on presentation of this ticket.",
          "Valid through 30 September 2026. One time per (e-)ticket.",
        ],
      },
      {
        title: "Bring your own vibe",
        lines: ["Blanket, cushion or low chair? Welcome.", "Outside alcohol and glass are not allowed on site"],
      },
    ],
  },
  travel: {
    eyebrow: "— Getting here",
    title1: "How to get here.",
    title2: "How to get home.",
    body: "Berg & Bos sits on a beautiful forest site just outside the centre of Apeldoorn. A short walk through the woods is part of the experience — wear comfortable shoes.",
    mapLabel: "Route map",
    address: "J.C. Wilslaan 21, Apeldoorn",
    mapsUrl: "https://maps.app.goo.gl/TM6ntXiRm8DtR1RP7",
    modes: [
      {
        title: "By bike",
        tag: "Recommended",
        body: "Free bike storage right by the entrance. From there it's about a 15-minute walk along the forest path to the meadow.",
      },
      {
        title: "By car",
        tag: "Parking",
        body: "Park at the main entrance of Berg & Bos. From the car park it's about a 15-minute walk through the park to the site.",
      },
      {
        title: "By public transport",
        tag: "Heads up",
        body: "A bus runs from Apeldoorn station towards J.C. Wilslaan. Then about a 15-minute walk. Late-night service is limited — check return times.",
      },
      {
        title: "Walking from the entrance",
        tag: "± 15 minutes",
        body: "From the main entrance of Berg & Bos it's about a 15-minute walk to the forest meadow. Signposted and lit with ambient lights for evening shows.",
      },
    ],
    freeParking: {
      badge: "Free parking",
      title: "Free parking via Asselsestraat",
      body: "Coming by car? At the end of the Asselsestraat in Apeldoorn, past Stayokay, you can park for free along the public road. This spot sits by the off-leash dog forest and the entrance to Stadspark Berg en Bos. Follow the traffic marshals' instructions.",
      note: "Note: this is a different location from the paid main car parks of Apenheul and Berg en Bos on J.C. Wilslaan.",
      walk: "From the parking spot, walk through the forest towards the festival grounds. Follow the local signage and expect a short walk.",
      cta: "Open route in Google Maps",
      disclaimer:
        "Parking is at your own risk. Availability may vary; always follow local traffic signs and instructions.",
      mapsUrl: "https://www.google.com/maps/search/?api=1&query=52.206375,5.919837",
    },
  },
  weather: {
    eyebrow: "— Weather & comfort",
    title1: "A summer evening",
    title2: "that always goes ahead.",
    body: "We watch outside — that's part of it. With the right layer of clothing and a good mood, every night, rain or shine, becomes one to remember.",
    tips: [
      {
        title: "Rain? No problem.",
        body: "The festival generally goes ahead in the rain. Only unsafe weather forces us to change plans.",
      },
      {
        title: "Dress warmly",
        body: "It cools down in the forest faster than you think. An extra sweater or jacket is always a good idea.",
      },
      {
        title: "Closed shoes",
        body: "It's a forest meadow. Sneakers or sturdy shoes are far more comfortable than flip-flops.",
      },
      {
        title: "Leave the umbrella at home",
        body: "A poncho or rain jacket keeps you dry and the view clear for others. Win win.",
      },
    ],
  },
  accessibility: {
    eyebrow: "— Accessibility",
    title: "Welcome — however you get to us.",
    body: "We do our best to let everyone enjoy a film night worry-free. Have specific needs? Let us know and we'll think along with you.",
    wheelchair: {
      title: "Wheelchair accessible",
      body: "The grounds are wheelchair accessible. There is a dedicated flat spot at the front for wheelchair users and companions.",
    },
    golfcart: {
      title: "Golf cart service",
      body: "For visitors with limited mobility a golf cart runs between the entrance and the forest meadow — no long walk required.",
    },
    question: {
      title: "Something we should know?",
      body: "Send us a message via the contact details at the bottom of the page. We'll gladly help you plan an evening where you feel at ease.",
    },
  },
  faq: {
    eyebrow: "— FAQ",
    title1: "A quick",
    title2: "check.",
    intro: "Question not listed? Send us a message via the contact details at the bottom.",
    items: [
      {
        q: "Do I get anything extra with my ticket?",
        a: "Yes! Your Openluchtfilmfestival ticket also unlocks a cinema gift. In September you can use it at Cinemajestic in Zutphen: on presentation of your (e-)ticket you get a second cinema ticket free. Enjoy another film — this time indoors. Valid through 30 September 2026, once per ticket.",
      },
      {
        q: "Can I reserve a fixed seat?",
        a: "No. A ticket gives access to the grounds and the covered grandstand, but does not reserve a specific seat. Early arrivals pick the best spot.",
      },
      {
        q: "What if it rains?",
        a: "The festival generally goes ahead in the rain. Bring warm clothes and a poncho if needed. Leave umbrellas at home — they block the view.",
      },
      {
        q: "Can I cancel my tickets?",
        a: "The cancellation policy depends on our ticketing partner. The exact terms will be shown with each ticket once sales start.",
      },
      {
        q: "Is the site wheelchair accessible?",
        a: "Yes. The forest meadow is gently sloping but accessible. A golf cart runs between the entrance and the site for visitors with limited mobility.",
      },
      {
        q: "Are dogs allowed?",
        a: "Pets are not allowed on the grounds. Assistance and guide dogs are of course very welcome.",
      },
      {
        q: "What time should I arrive?",
        a: "The grounds open at 20:00 — well before the start. Films begin when it's dark enough, between 21:00 and 21:15. Arrive early to grab a bite and pick your spot on the forest meadow.",
      },
      {
        q: "Can I buy food and drinks on site?",
        a: "Yes. Food and drinks are available on site. Perfect to settle in before the film.",
      },
      {
        q: "How does parking work?",
        a: "Parking at the main entrance of Berg & Bos. From the car park it's about a 15-minute walk to the forest meadow.",
      },
      {
        q: "Is there covered seating?",
        a: "Yes, there are 500 covered seats on the grandstand, in addition to 1,500 seats.",
      },
      {
        q: "Can I buy tickets at the door?",
        a: "Yes, subject to availability. You can buy tickets at the door on the night, as long as the screening isn't sold out. Buying online in advance is recommended — it guarantees your spot and avoids the queue.",
      },
    ],
  },
  finalCta: {
    eyebrow: "● 2026 Edition · 14 — 22 August · Berg & Bos",
    title1: "Make sure you're there",
    title2: "this summer.",
    body: "Nine nights, one place, one summer. Book early for the most popular film nights — they tend to sell out surprisingly fast.",
    note: "● Online or at the door · while stocks last",
  },
  footer: {
    tagline:
      "Nine summer evenings of open-air cinema on the forest meadow of Stadspark Berg & Bos. Great films, outdoors in the green, in the heart of summer.",
    edition: "● 2026 Edition · 14 — 22 August",
    location: "Location",
    contact: "Contact",
    followUs: "Follow us",
    rights: "All rights reserved",
    copyright: "© {year} Openluchtfilmfestival Apeldoorn",
    privacy: "Privacy",
    terms: "Terms",
    ticketPartner: "Ticketing partner",
  },
  sticky: { teaser: "Be the first in line", dates: "● 14 — 22 Aug · Berg & Bos" },
  seo: {
    home: {
      title: "Open-Air Film Festival Apeldoorn · 14 — 22 August 2026",
      description:
        "Nine summer evenings of open-air cinema at Stadspark Berg & Bos in Apeldoorn. Great films under the stars, food and drinks on site and summer vibes.",
      ogTitle: "Open-Air Film Festival Apeldoorn — 2026 Edition",
      ogDescription:
        "14 to 22 August 2026 — open-air cinema on the forest meadow of Stadspark Berg & Bos. Don't miss the start of ticket sales.",
    },
  },
};

const de: Dict = {
  nav: {
    programma: "Programm",
    beleving: "Erlebnis",
    sfeer: "Atmosphäre",
    praktisch: "Besuch",
    faq: "FAQ",
    tickets: "Tickets kaufen",
    dates: "14. — 22. Aug. 2026",
    menuOpen: "Menü öffnen",
    menuClose: "Menü schließen",
    partners: "Partner",
    volunteer: "Freiwilliger werden?",
    terrain: "Gelände",
    gettingHere: "Anreise",
    weatherComfort: "Wetter & Komfort",
    accessibility: "Barrierefreiheit",
  },
  common: {
    buyTickets: "Tickets kaufen",
    viewProgram: "Programm ansehen",
    receiveProgram: "Programm erhalten",
    backToTop: "Nach oben",
    moreInfo: "Mehr Infos",
    language: "Sprache",
    openInMaps: "In Google Maps öffnen →",
  },
  hero: {
    badge: "32. AUSGABE · TICKETS BALD IM VORVERKAUF",
    title1: "Große Filme.",
    title2: "Unter dem Sternenhimmel",
    title3: "im Stadspark Berg & Bos.",
    intro:
      "Neun Sommerabende lang verwandelt sich die Waldwiese im Stadspark Berg & Bos in das Freilichtkino von Apeldoorn. Sobald die Sonne untergeht und die Leinwand erstrahlt, beginnt ein Filmabend, den es so nirgendwo anders gibt.",
    dates: "Spieltage",
    datesValue: "14. — 22. Aug.",
    location: "Ort",
    locationValue: "Berg & Bos · Apeldoorn",
    capacity: "Kapazität",
    capacityOutside: "2.000 draußen",
    capacityCovered: "500 überdacht",
    note: "Beliebte Abende sind schnell ausverkauft",
    startLabel: "Beginn",
    startValue: "20:00 — 21:15",
    startSub: "Gelände ab 20:00 · Film startet, wenn es dunkel genug ist (21:00–21:15)",
    tickerNow: "● Now playing soon — Ausgabe 2026",
    tickerTime: "Gelände ab 20:00 · Film startet, wenn es dunkel genug ist (21:00–21:15)",
    tickerParking: "Parken · 15 Min. Fußweg ab Eingang",
    heroAlt: "Freilichtkino im Wald von Berg & Bos am Abend",
  },
  why: {
    eyebrow: "— Warum du dabei sein willst",
    title: "Ein Sommerabend, den du nicht im gewöhnlichen Kino verschwendest.",
    body: "Große Filme, eine riesige Leinwand, der Wald rundherum und der Duft des Sommers. Kein Popcorngeruch, kein roter Plüsch — dafür Dämmerung, Sterne und das leise Summen des Publikums.",
    stats: [
      { value: "9", suffix: "Abende", caption: "Vom 14. bis 22. August" },
      { value: "2.000", suffix: "Plätze draußen", caption: "Platzwahl auf der Wiese" },
      { value: "500", suffix: "überdacht", caption: "Trocken auf der Tribüne" },
      { value: "200 m²", suffix: "große Leinwand", caption: "Mitten im Wald" },
    ],
    reasons: [
      {
        title: "Ein Kinosaal im Wald",
        body: "Keine Decke, keine Wände — nur das Blätterdach von Stadspark Berg & Bos und eine Leinwand, die erstrahlt, sobald die Dämmerung einsetzt.",
      },
      {
        title: "Apeldoorns Sommertradition",
        body: "Seit Jahren der Abend, auf den sich Apeldoorn freut. Ein Erlebnis, das noch lange nach dem Abspann nachklingt.",
      },
      {
        title: "Essen, trinken, bleiben",
        body: "Gastronomie auf dem Gelände vor, während und nach dem Film. Setz dich mit Freunden zusammen, teil eine Flasche Wein, lass den Abend dauern.",
      },
      {
        title: "Entspannt hin und zurück",
        body: "Parken am Haupteingang, kostenloser Fahrradabstellplatz und ein kurzer Waldspaziergang zum Gelände.",
      },
    ],
  },
  email: {
    badge: "Early Access",
    title1: "Sei als Erstes dabei,",
    title2: "vor dem Rest von Apeldoorn.",
    body: "Erhalte das Programm in deinen Posteingang, einen privaten Link zum Vorverkauf und sofort Bescheid, wenn beliebte Abende fast ausverkauft sind.",
    bullets: [
      "Programm direkt in deinem Posteingang",
      "24 Stunden vor öffentlichem Verkaufsstart",
      "Kein Rauschen. Nur Festival-Updates.",
    ],
    firstName: "Vorname",
    firstNamePlaceholder: "Z. B. Alex",
    email: "E-Mail-Adresse",
    emailPlaceholder: "du@beispiel.de",
    submit: "Halte mich auf dem Laufenden",
    submitting: "Einen Moment…",
    success: "Du stehst auf der Liste. Wir informieren dich als Erstes, sobald der Ticketverkauf startet.",
    disclaimer: "Kein Spam. Nur relevante Updates zum Festival. Abmelden ist jederzeit möglich.",
    required: "Bitte füll dieses Feld aus",
    invalidEmail: "Bitte gib eine gültige E-Mail-Adresse ein",
  },
  gallery: {
    eyebrow: "— Atmosphäre",
    title1: "Das letzte Licht über den Bäumen.",
    title2: "Die Leinwand erstrahlt.",
    body: "Das leise Summen des Publikums, der Duft eines Sommerabends, die ersten Klänge, die über die Wiese rollen. So fühlt sich ein Abend in Stadspark Berg & Bos an.",
    captions: {
      screen: "Die große Leinwand · Dämmerung",
      audience: "2.000 Sitzplätze",
      horeca: "Gastronomie · vor und nach dem Film",
      couple: "Gemeinsam unter den Sternen",
      drinks: "Ein Abend, der dauern darf",
    },
    alts: {
      screen: "Kinoleinwand im Wald am Abend",
      audience: "Publikum auf der Waldwiese",
      horeca: "Gastronomiestände mit Stimmungslicht im Wald",
      couple: "Paar schaut gemeinsam einen Open-Air-Film",
      drinks: "Glas Wein bei Lichterketten am Abend",
    },
  },
  practical: {
    eyebrow: "— Auf dem Gelände",
    title1: "Alles, was du wissen musst,",
    title2: "bevor du kommst.",
    cards: [
      { title: "Ort", lines: ["Waldwiese Berg & Bos", "J.C. Wilslaan 21, 7313 HK Apeldoorn"] },
      {
        title: "Sitzplätze",
        lines: [
          "2.000 Plätze draußen · 500 auf überdachter Tribüne",
          "Platzwahl — kein fester Sitz pro Ticket",
          "Eigener niedriger Stuhl ist erlaubt, aber nicht nötig",
        ],
      },
      {
        title: "Gastronomie vor Ort",
        lines: ["Snacks, Getränke und Sommercocktails", "Geöffnet ab 20:00 Uhr bis weit nach dem Abspann"],
      },
      {
        title: "Toiletten & Sanitätsdienst",
        lines: ["Kostenlose Toiletten auf dem Gelände", "Sanitätsdienst an jedem Abend vor Ort"],
      },
      {
        title: "Kino-Geschenk",
        lines: [
          "Bewahre dein Ticket auf: im September gibt's bei Cinemajestic Zutphen gegen Vorlage dieses Tickets ein 2. Kinoticket gratis.",
          "Gültig bis 30. September 2026. Einmalig pro (E-)Ticket.",
        ],
      },
      {
        title: "Eigene Atmosphäre erlaubt",
        lines: [
          "Decke, Kissen oder niedriger Stuhl? Willkommen.",
          "Eigener Alkohol und Glas sind auf dem Gelände nicht erlaubt",
        ],
      },
    ],
  },
  travel: {
    eyebrow: "— Anreise",
    title1: "So kommst du hin.",
    title2: "So kommst du nach Hause.",
    body: "Berg & Bos liegt auf einem wunderschönen Waldgelände direkt am Rand des Zentrums von Apeldoorn. Ein kurzer Waldspaziergang gehört zum Abend dazu — bequeme Schuhe empfohlen.",
    mapLabel: "Routenkarte",
    address: "J.C. Wilslaan 21, Apeldoorn",
    mapsUrl: "https://maps.app.goo.gl/TM6ntXiRm8DtR1RP7",
    modes: [
      {
        title: "Mit dem Fahrrad",
        tag: "Empfehlung",
        body: "Kostenloser Fahrradabstellplatz direkt am Eingang. Von dort sind es etwa 15 Minuten Fußweg über den Waldweg zur Wiese.",
      },
      {
        title: "Mit dem Auto",
        tag: "Kostenlos parken",
        body: "Parke kostenlos am Haupteingang von Berg & Bos. Vom Parkplatz läufst du in rund 15 Minuten durch den Park zum Gelände.",
      },
      {
        title: "Mit dem ÖPNV",
        tag: "Achtung",
        body: "Vom Bahnhof Apeldoorn fährt ein Bus Richtung J.C. Wilslaan. Danach noch ca. 15 Minuten Fußweg. Nach Ende ist der ÖPNV oft eingeschränkt — Rückfahrzeiten prüfen.",
      },
      {
        title: "Zu Fuß ab Eingang",
        tag: "± 15 Minuten",
        body: "Vom Haupteingang von Berg & Bos läufst du in etwa 15 Minuten zur Waldwiese. Ausgeschildert und bei Abendvorstellungen stimmungsvoll beleuchtet.",
      },
    ],
    freeParking: {
      badge: "Kostenlos parken",
      title: "Kostenlos parken über die Asselsestraat",
      body: "Kommst du mit dem Auto? Am Ende der Asselsestraat in Apeldoorn, hinter Stayokay, kannst du kostenlos an der öffentlichen Straße parken. Dieser Parkplatz liegt am Hunde-Freilaufwald und am Eingang des Stadtparks Berg en Bos. Folge den Anweisungen der Verkehrshelfer.",
      note: "Hinweis: Dies ist ein anderer Ort als die kostenpflichtigen Hauptparkplätze von Apenheul und Berg en Bos an der J.C. Wilslaan.",
      walk: "Vom Parkplatz läufst du durch den Wald in Richtung Festivalgelände. Folge der örtlichen Beschilderung und rechne mit einem kurzen Fußweg.",
      cta: "Route in Google Maps öffnen",
      disclaimer:
        "Parken auf eigene Verantwortung. Verfügbarkeit kann variieren; folge immer lokalen Verkehrsschildern und Anweisungen.",
      mapsUrl: "https://www.google.com/maps/search/?api=1&query=52.206375,5.919837",
    },
  },
  weather: {
    eyebrow: "— Wetter & Komfort",
    title1: "Ein Sommerabend,",
    title2: "der immer stattfindet.",
    body: "Wir schauen draußen — das gehört dazu. Mit der richtigen Kleidung und guter Stimmung wird jeder Abend, ob Sonne oder Regen, zu einem, an den du dich erinnern wirst.",
    tips: [
      {
        title: "Regen? Kein Problem.",
        body: "Das Festival findet grundsätzlich auch bei Regen statt. Nur bei unsicherem Wetter weichen wir aus.",
      },
      {
        title: "Zieh dich warm an",
        body: "Im Wald kühlt es schneller ab, als du denkst. Ein zusätzlicher Pullover oder eine Jacke ist immer eine gute Idee.",
      },
      {
        title: "Geschlossene Schuhe",
        body: "Es ist eine Waldwiese. Sneaker oder feste Schuhe sind bequemer als Flip-Flops.",
      },
      {
        title: "Lass den Regenschirm zu Hause",
        body: "Ein Poncho oder eine Regenjacke hält dich trocken und die Sicht der anderen frei. Win-win.",
      },
    ],
  },
  accessibility: {
    eyebrow: "— Barrierefreiheit",
    title: "Willkommen — egal wie du zu uns kommst.",
    body: "Wir geben unser Bestes, damit alle einen Filmabend unbeschwert genießen können. Hast du besondere Wünsche? Sag Bescheid, wir denken gerne mit.",
    wheelchair: {
      title: "Rollstuhlgerecht",
      body: "Das Gelände ist rollstuhlgerecht. Es gibt einen eigenen, ebenen Platz vorne für Rollstuhlfahrende und Begleitpersonen.",
    },
    golfcart: {
      title: "Golfcart-Service",
      body: "Für mobilitätseingeschränkte Gäste verkehrt ein Golfcart zwischen Eingang und Waldwiese — kein langer Fußweg nötig.",
    },
    question: {
      title: "Sollen wir etwas berücksichtigen?",
      body: "Schreib uns gerne über die Kontaktdaten am Seitenende. Wir helfen dir gerne, einen Abend zu planen, an dem du dich wohlfühlst.",
    },
  },
  faq: {
    eyebrow: "— FAQ",
    title1: "Schnell",
    title2: "nachschauen.",
    intro: "Deine Frage ist nicht dabei? Schreib uns über die Kontaktdaten am Seitenende.",
    items: [
      {
        q: "Bekomme ich zu meinem Ticket noch etwas extra?",
        a: "Ja! Dein Ticket vom Openluchtfilmfestival ist zugleich dein Zugang zu einem Kino-Geschenk. Im September kannst du es bei Cinemajestic in Zutphen einlösen: gegen Vorlage deines (E-)Tickets erhältst du ein zweites Kinoticket gratis. So genießt du noch einmal Film — diesmal drinnen. Gültig bis 30. September 2026, einmalig pro Ticket.",
      },
      {
        q: "Kann ich einen festen Sitzplatz reservieren?",
        a: "Nein. Ein Ticket gibt Zutritt zum Gelände und zur überdachten Tribüne, reserviert aber keinen festen Platz. Wer früh da ist, sucht sich den besten Platz aus.",
      },
      {
        q: "Was passiert bei Regen?",
        a: "Das Festival findet grundsätzlich auch bei Regen statt. Bring warme Kleidung und eventuell einen Poncho mit. Lass Regenschirme zu Hause — sie versperren die Sicht.",
      },
      {
        q: "Kann ich meine Tickets stornieren?",
        a: "Die Stornierungsbedingungen hängen von unserem Ticketpartner ab. Sobald der Verkauf startet, findest du die genauen Bedingungen bei jedem Ticket.",
      },
      {
        q: "Ist das Gelände rollstuhlgerecht?",
        a: "Ja. Die Waldwiese ist leicht hügelig, aber zugänglich. Für mobilitätseingeschränkte Gäste verkehrt ein Golfcart zwischen Eingang und Gelände.",
      },
      {
        q: "Sind Hunde erlaubt?",
        a: "Haustiere sind auf dem Gelände nicht erlaubt. Begleit- und Assistenzhunde sind selbstverständlich willkommen.",
      },
      {
        q: "Wann sollte ich da sein?",
        a: "Das Gelände ist ab 20:00 Uhr geöffnet — lange vor Beginn. Die Filme beginnen, wenn es dunkel genug ist, zwischen 21:00 und 21:15 Uhr. Komm entspannt früh, dann kannst du noch etwas essen und dir einen Platz auf der Waldwiese aussuchen.",
      },
      {
        q: "Kann ich vor Ort essen und trinken kaufen?",
        a: "Ja. Vor Ort gibt es Gastronomie mit Snacks und Getränken. Perfekt, um schon vor dem Film anzukommen.",
      },
      {
        q: "Wie funktioniert das Parken?",
        a: "Parken am Haupteingang von Berg & Bos ist kostenlos. Vom Parkplatz sind es etwa 15 Minuten Fußweg zur Waldwiese.",
      },
      {
        q: "Gibt es überdachte Plätze?",
        a: "Ja, es gibt 500 überdachte Sitzplätze auf der Tribüne, zusätzlich zu 2.000 Sitzplätzen.",
      },
      {
        q: "Kann ich Tickets an der Abendkasse kaufen?",
        a: "Ja, solange Plätze verfügbar sind. Du kannst Tickets an der Abendkasse am selben Abend kaufen, solange die Vorstellung nicht ausverkauft ist. Vorab online zu buchen ist empfehlenswert — dann ist dein Platz sicher und du vermeidest die Schlange.",
      },
    ],
  },
  finalCta: {
    eyebrow: "● Ausgabe 2026 · 14. — 22. August · Berg & Bos",
    title1: "Sei dabei",
    title2: "diesen Sommer.",
    body: "Neun Abende, ein Ort, ein Sommer. Buche rechtzeitig für die beliebtesten Filmabende — sie sind oft überraschend schnell weg.",
    note: "● Online oder an der Abendkasse · solange Vorrat reicht",
  },
  footer: {
    tagline:
      "Neun Sommerabende Freilichtkino auf der Waldwiese von Berg & Bos. Große Filme, draußen im Grünen, mitten im Sommer.",
    edition: "● Ausgabe 2026 · 14. — 22. August",
    location: "Ort",
    contact: "Kontakt",
    followUs: "Folge uns",
    rights: "Alle Rechte vorbehalten",
    copyright: "© {year} Openluchtfilmfestival Apeldoorn",
    privacy: "Datenschutz",
    terms: "AGB",
    ticketPartner: "Ticketpartner",
  },
  sticky: { teaser: "Sei als Erstes dabei", dates: "● 14. — 22. Aug. · Berg & Bos" },
  seo: {
    home: {
      title: "Freilicht-Filmfestival Apeldoorn · 14. — 22. August 2026",
      description:
        "Neun Sommerabende Freilichtkino auf der Waldwiese von Berg & Bos in Apeldoorn. Große Filme unter den Sternen, Gastronomie vor Ort und Sommerstimmung.",
      ogTitle: "Freilicht-Filmfestival Apeldoorn — Ausgabe 2026",
      ogDescription:
        "14. bis 22. August 2026 — Freilichtkino auf der Waldwiese von Berg & Bos. Verpasse den Start des Ticketverkaufs nicht.",
    },
  },
};

export const translations: Record<Lang, Dict> = { nl, en, de };

// Deep fallback: any missing leaf falls back to NL automatically because the
// objects share the same shape; types enforce completeness for `nl` only via
// the central `Dict` and we runtime-merge to be safe.
function mergeFallback<T>(base: T, override: Partial<T>): T {
  if (Array.isArray(base)) return (override as T) ?? base;
  if (base && typeof base === "object") {
    const out: any = Array.isArray(base) ? [] : { ...base };
    for (const k of Object.keys(base as any)) {
      const o = (override as any)?.[k];
      out[k] = o === undefined || o === null ? (base as any)[k] : mergeFallback((base as any)[k], o);
    }
    return out;
  }
  return (override as any) ?? base;
}

export const getDict = (lang: Lang): Dict =>
  lang === "nl" ? nl : mergeFallback(nl, translations[lang] as Partial<Dict>);
